import { ActionContext } from 'vuex'
import { RootState } from '@/store/state'
import { deepCopyObject } from '@/utils/object'

export type EntityType =
  | 'Question'
  | 'QuestionSearchSuggestion'
  | 'QuestionCounts'
  | 'PreviewQuestion'
  | 'Comment'
  | 'CommentReply'
  | 'PreviewComment'
  | 'Tag'
  | 'MyReaction'
  | 'MyCommentReplyReaction'
  | 'MyQuestionReaction'
  | 'Notification'
  | 'Skill'
  | 'SkillCategoryList'
  | 'AllQuestionsCount'
  | 'VideoLinks'
  | 'SpContentList'
  | 'MyCompanyUser'
  | 'MyCompanyUserSummary'
  | 'Survey'
  | 'SkillMemoHistory'
  | 'Hitokoto'

export type FetchHistory = {
  expiry: Date
  fetchTime: Date
  keys: string[]
}

type State = {
  history: {
    [key: string]: FetchHistory
  }
  entity: Record<EntityType, Record<string, any>>
}

const initialState: State = {
  entity: {
    Question: {},
    QuestionSearchSuggestion: {},
    QuestionCounts: {},
    PreviewQuestion: {},
    Comment: {},
    CommentReply: {},
    MyCommentReplyReaction: {},
    PreviewComment: {},
    Tag: {},
    MyReaction: {},
    MyQuestionReaction: {},
    Notification: {},
    Skill: {},
    SkillCategoryList: {},
    AllQuestionsCount: {},
    VideoLinks: {},
    SpContentList: {},
    MyCompanyUser: {},
    MyCompanyUserSummary: {},
    Survey: {},
    SkillMemoHistory: {},
    Hitokoto: {}
  },
  history: {}
}

export default {
  state: deepCopyObject(initialState) as State,
  mutations: {
    SET_HISTORY: (
      state: State,
      { key, history }: { key: string; history: FetchHistory }
    ) => {
      state.history[key] = history
    },
    EXPIRE_HISTORY: (state: State, key: string) => {
      const history = state.history[key]
      const now = new Date()
      if (!history || history.expiry < now) return
      state.history[key] = { ...history, expiry: now }
    },
    SET_ENTITY: (
      state: State,
      { type, key, entity }: { type: EntityType; entity: any; key: string }
    ) => {
      state.entity[type][key] = entity
    },
    SET_ENTITIES: (
      state: State,
      { type, entities }: { type: EntityType; entities: { [key: string]: any } }
    ) => {
      const additionalEntities = {}
      Object.keys(entities).forEach((key) => {
        additionalEntities[key] = entities[key]
      })
      state.entity[type] = {
        ...state.entity[type],
        ...additionalEntities
      }
    },
    REMOVE_ENTITY: (
      state: State,
      { type, key }: { type: EntityType; key: string }
    ) => {
      const entities = {
        ...state.entity[type]
      }
      delete entities[key]
      state.entity[type] = entities
    },
    CLEAR: (state: State) => {
      Object.assign(state, deepCopyObject(initialState))
    }
  },
  getters: {
    getHistory(state: State) {
      return (key: string) => {
        const history = state.history[key]
        if (!history) return null
        const now = new Date()
        if (history.expiry < now) return null
        return history
      }
    },
    getEntity(state: State) {
      return (type: EntityType, key: string) => {
        return state.entity[type][key]
      }
    },
    getEntities(state: State) {
      return (type: EntityType, keys: string[]) => {
        const entities = state.entity[type]
        return keys
          .map((key) => {
            return entities[key]
          })
          .filter((entity) => entity)
      }
    },
    getAllEntities(state: State) {
      return (type: EntityType) => {
        return Object.values(state.entity[type])
      }
    },
    getAllEntitiesAsObject(state: State) {
      return (type: EntityType) => {
        return state.entity[type]
      }
    }
  },
  actions: {
    setHistory: (
      { commit }: ActionContext<State, RootState>,
      payload: { key: string; history: FetchHistory }
    ) => {
      commit('SET_HISTORY', payload)
    },
    expireHistory: (
      { commit }: ActionContext<State, RootState>,
      payload: string
    ) => {
      commit('EXPIRE_HISTORY', payload)
    },
    setEntity: (
      { commit }: ActionContext<State, RootState>,
      payload: { type: EntityType; entity: any; key: string }
    ) => {
      commit('SET_ENTITY', payload)
    },
    setEntities: (
      { commit }: ActionContext<State, RootState>,
      payload: { type: EntityType; entities: { [key: string]: any } }
    ) => {
      commit('SET_ENTITIES', payload)
    },
    removeEntity: (
      { commit }: ActionContext<State, RootState>,
      payload: { type: EntityType; key: string }
    ) => {
      commit('REMOVE_ENTITY', payload)
    },
    clear: ({ commit }: ActionContext<State, RootState>) => {
      commit('CLEAR')
    }
  },
  namespaced: true
}
